import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';

export default function Shifts() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.shifts.list(params);
      const raw = data.shifts || data.data || data;
      const list = Array.isArray(raw) ? raw : raw?.data || [];
      setRows(list);
      setMeta(!Array.isArray(raw) && raw?.current_page
        ? { current_page: raw.current_page, last_page: raw.last_page, total: raw.total, per_page: raw.per_page, per_page_options: raw.per_page_options || data.per_page_options }
        : null);
    } catch (err) {
      showError('Failed to load shifts', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete(`shift "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.shifts.remove(row.id);
      toast('success', '', 'Shift deleted');
      load();
    } catch (err) {
      showError('Failed to delete shift', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Shifts"
        subtitle="Morning, evening and night shifts."
        crumbs={[{ label: 'HR' }, { label: 'Shifts' }]}
        actions={hasPermission('create-employees') && (
          <button className="btn btn-primary" onClick={() => navigate('/shifts/new')}><FiPlus /> Add Shift</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="shifts" label="shifts" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No shifts found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Shift Name', render: (r) => <b>{r.name}</b> },
              { key: 'short_name', label: 'Short Name' },
              { key: 'start_time', label: 'Start Time' },
              { key: 'end_time', label: 'End Time' },
              { key: 'is_active', label: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'inactive'} /> },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('update-employees') && <button className="btn-icon" onClick={() => navigate(`/shifts/${r.id}/edit`)}><FiEdit2 /></button>}
                  {hasPermission('delete-employees') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}
